import { useCallback, useMemo, useState } from 'react';
import { formatCurrency, parseDollarsToCents } from '@/lib/format-currency';

export interface UseAmountInputMessages {
  /**
   * Builder for the below-min error. Defaults to
   * `Minimum amount is ${formatCurrency(minCents)}.`
   */
  belowMin?: (minCents: number) => string;
  /**
   * Builder for the above-max error. Defaults to
   * `Maximum amount is ${formatCurrency(maxCents)}.`
   */
  aboveMax?: (maxCents: number) => string;
  /** Override for the "exceeds balance" message. */
  aboveBalance?: string;
}

export interface UseAmountInputOptions {
  /** Minimum allowed amount in cents (inclusive). */
  minCents: number;
  /** Optional maximum allowed amount in cents (inclusive). */
  maxCents?: number;
  /**
   * Optional available balance in cents. When provided, the hook enforces
   * `amount <= balanceCents` (withdraw mode). Independent of `maxCents`.
   */
  balanceCents?: number;
  /** Optional copy overrides for the four validation states. */
  messages?: UseAmountInputMessages;
}

export interface UseAmountInputResult {
  /** Raw string the user typed. */
  amountInput: string;
  /** Parsed cents, or null when input is empty / invalid. */
  amountCents: number | null;
  /** True once the user has interacted with the input. */
  touched: boolean;
  /** Server-side error string (set by the caller via setServerError). */
  serverError: string;
  /** Updates the raw input and marks the field touched. Clears serverError. */
  setAmountInput: (next: string) => void;
  /** Force the touched flag, e.g. on submit. */
  markTouched: () => void;
  /** Clear the touched flag without clearing the input. */
  clearTouched: () => void;
  /** Set an api-derived error message. */
  setServerError: (message: string) => void;
  /** Clear input, touched flag, and serverError. */
  reset: () => void;
  /**
   * Derived client-side validation error. Null until `touched` flips on, and
   * null for an empty field (handled by disabling actions, not an error).
   * Order: below-min → above-max → above-balance.
   */
  clientError: string | null;
}

/**
 * Shared state + validation for the deposit and withdraw amount fields.
 * Both forms previously hand-rolled near-identical `amountInput`,
 * `touched`, and `serverError` state with the same validation logic; this
 * hook is the deduplicated source.
 */
export function useAmountInput(options: UseAmountInputOptions): UseAmountInputResult {
  const { minCents, maxCents, balanceCents, messages } = options;

  const [amountInput, setAmountInputState] = useState('');
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState('');

  const amountCents = useMemo<number | null>(() => parseDollarsToCents(amountInput), [amountInput]);

  const setAmountInput = useCallback((next: string) => {
    setAmountInputState(next);
    setTouched(true);
    setServerError('');
  }, []);

  const markTouched = useCallback(() => {
    setTouched(true);
  }, []);

  const clearTouched = useCallback(() => {
    setTouched(false);
    setServerError('');
  }, []);

  const reset = useCallback(() => {
    setAmountInputState('');
    setTouched(false);
    setServerError('');
  }, []);

  const clientError = useMemo<string | null>(() => {
    if (!touched) return null;
    // An empty field is not an error — the action buttons are disabled instead
    // (so deleting a typed value no longer surfaces an "Enter an amount." error).
    if (amountCents == null) return null;
    if (amountCents < minCents) {
      return messages?.belowMin?.(minCents) ?? `Minimum amount is ${formatCurrency(minCents)}.`;
    }
    if (maxCents != null && amountCents > maxCents) {
      return messages?.aboveMax?.(maxCents) ?? `Maximum amount is ${formatCurrency(maxCents)}.`;
    }
    if (balanceCents != null && amountCents > balanceCents) {
      return messages?.aboveBalance ?? 'Amount exceeds your available balance.';
    }
    return null;
  }, [touched, amountCents, minCents, maxCents, balanceCents, messages]);

  return {
    amountInput,
    amountCents,
    touched,
    serverError,
    setAmountInput,
    markTouched,
    clearTouched,
    setServerError,
    reset,
    clientError,
  };
}
